import { useChatClient } from '@/context/ChatClientContext'
import { useStreamChat } from '@/hooks/useStreamChat'
import BusinessCenterOutlinedIcon from '@mui/icons-material/BusinessCenterOutlined'
import Chat from '@mui/icons-material/Chat'
import Close from '@mui/icons-material/Close'
import { useCallback, useEffect, useRef } from 'react'

import { useTheme } from '@mui/material'
import Box from '@mui/material/Box'
import IconButton from '@mui/material/IconButton'
import Tab from '@mui/material/Tab'
import Tabs from '@mui/material/Tabs'
import Typography from '@mui/material/Typography'

import { Channel, DefaultGenerics } from 'stream-chat'
import ChannelListItem from '../ChannelListItem'
import { StyledBadge } from '../StyledBadge'
import { ChannelOptions } from '../types'
import CaseChannelList from './CaseChannelList'

const scrollDistanceFromChannelListBottomToTriggerFetch = 100

const ChannelList = () => {
  const theme = useTheme()

  const {
    selectedTab,
    setSelectedTab,
    getChannelListItemData,
    messageDrawerOpen,
    currentCaseChatId,
    getMyCaseChannels,
    getMyDirectChannels,
    closeMessageDrawer,
    getChannelsByCaseId,
    channels,
    channelsLoading,
    hasMoreChannels,
    setChannels,
    setChannelsLoading,
    setHasMoreChannels,
  } = useStreamChat()

  const { unreadCaseMessages, unreadDirectMessages } = useChatClient()

  const selectedTabLabel = () => {
    switch (selectedTab) {
      case ChannelOptions.DirectMessages:
        return 'Direct Messages'
      case ChannelOptions.CaseMessages:
        return 'Case Messages'
      default:
        return 'Messages'
    }
  }

  const observer = useRef<IntersectionObserver | null>(null)

  const fetchChannels = useCallback(async () => {
    if (!hasMoreChannels) return
    setChannelsLoading(true)

    try {
      let newChannels: Channel<DefaultGenerics>[] = []

      if (currentCaseChatId && selectedTab === ChannelOptions.CaseMessages) {
        newChannels = await getChannelsByCaseId(currentCaseChatId)
      } else {
        switch (selectedTab) {
          case ChannelOptions.DirectMessages:
            newChannels = await getMyDirectChannels()
            break
          case ChannelOptions.CaseMessages:
            newChannels = await getMyCaseChannels()
            break
          default:
            throw new Error('No selected tab')
        }
      }

      setChannels(prev => {
        if (newChannels.length <= prev[selectedTab].length) {
          setHasMoreChannels(false)
        }

        return {
          ...prev,
          [selectedTab]: newChannels,
        }
      })
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to fetch channels', error)
      setHasMoreChannels(false)
    } finally {
      setChannelsLoading(false)
    }
  }, [
    hasMoreChannels,
    setChannelsLoading,
    currentCaseChatId,
    selectedTab,
    setChannels,
    getChannelsByCaseId,
    getMyDirectChannels,
    getMyCaseChannels,
    setHasMoreChannels,
  ])

  const lastChannelElementRef = useCallback(
    (node: HTMLElement | null) => {
      if (channelsLoading) return
      if (channels[selectedTab].length === 0) return
      if (!hasMoreChannels) return

      if (observer.current) observer.current.disconnect()

      observer.current = new IntersectionObserver(
        entries => {
          if (entries[0].isIntersecting && hasMoreChannels) {
            fetchChannels()
          }
        },
        {
          rootMargin: `0px 0px ${scrollDistanceFromChannelListBottomToTriggerFetch}px 0px`,
        }
      )

      if (node) observer.current.observe(node)
    },
    [channelsLoading, channels, selectedTab, hasMoreChannels, fetchChannels]
  )

  useEffect(() => {
    setHasMoreChannels(true)
  }, [selectedTab, currentCaseChatId, setHasMoreChannels])

  useEffect(() => {
    if (!messageDrawerOpen) return

    fetchChannels()
  }, [fetchChannels, messageDrawerOpen])

  const channelList =
    selectedTab === ChannelOptions.CaseMessages ? (
      <CaseChannelList
        channels={channels[selectedTab]}
        loading={channelsLoading}
        getChannelListItemData={getChannelListItemData}
        lastChannelElementRef={lastChannelElementRef}
      />
    ) : (
      channels[selectedTab].map((channel, idx) => {
        const channelItemData = getChannelListItemData(channel)

        if (!channelItemData) return null

        return (
          <ChannelListItem
            key={channel.id}
            ref={
              idx === channels[selectedTab].length - 1
                ? lastChannelElementRef
                : undefined
            }
            {...channelItemData}
          />
        )
      })
    )

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: 2,
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Typography variant="h6" noWrap>
          {selectedTabLabel()}
        </Typography>
        <IconButton onClick={closeMessageDrawer}>
          <Close />
        </IconButton>
      </Box>

      <Box
        sx={{
          flexGrow: 1,
          overflowY: 'auto',
        }}
      >
        {channelList}
      </Box>

      <Tabs
        value={selectedTab}
        onChange={(_, newValue) => setSelectedTab(newValue)}
        variant="fullWidth"
        sx={{
          borderTop: `1px solid ${theme.palette.divider}`,
          position: 'static',
          flexShrink: 0,
        }}
      >
        <Tab
          label="Case Messages"
          value={ChannelOptions.CaseMessages}
          icon={
            <StyledBadge
              badgeContent={unreadCaseMessages}
              color="error"
              sx={{
                '.MuiBadge-badge': {
                  borderRadius: '100%',
                },
              }}
            >
              <BusinessCenterOutlinedIcon />
            </StyledBadge>
          }
        />
        <Tab
          label="Direct Messages"
          value={ChannelOptions.DirectMessages}
          icon={
            <StyledBadge
              badgeContent={unreadDirectMessages}
              color="error"
              sx={{
                '.MuiBadge-badge': {
                  borderRadius: '100%',
                },
              }}
            >
              <Chat />
            </StyledBadge>
          }
        />
      </Tabs>
    </>
  )
}

export default ChannelList
